import { useEffect, useRef, useState } from 'react'

import {
  COST_BOM_DEMO_STRINGS,
  SAMPLE_PRODUCTS,
} from '~/widgets/CostBomDemo/strings'

type Sample = (typeof SAMPLE_PRODUCTS)[number]

const DEFAULT = SAMPLE_PRODUCTS[0]

export function HeroProductDropdown() {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [product, setProduct] = useState<Sample['product']>(DEFAULT.product)
  const [country, setCountry] = useState<Sample['country']>(DEFAULT.country)
  const [activeKey, setActiveKey] = useState<Sample['key']>(DEFAULT.key)

  useEffect(() => {
    if (window.setHeroProductImage) {
      window.setHeroProductImage(DEFAULT.product)
    }
  }, [])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const pickSample = (sample: (typeof SAMPLE_PRODUCTS)[number]) => {
    setProduct(sample.product)
    setCountry(sample.country)
    setActiveKey(sample.key)
    setOpen(false)

    if (window.setHeroProductImage) window.setHeroProductImage(sample.product)

    const hero = document.getElementById('hero')
    if (
      window.heroShowSample &&
      window.heroPanelsReady &&
      hero?.getAttribute('data-hero-phase') === 'idle'
    ) {
      window.heroShowSample(sample.key)
    }
  }

  const openConversion = () => {
    setOpen(false)
    window.openHeroConversionModal?.()
  }

  return (
    <div
      ref={wrapRef}
      className={`hero-dd hero-dd--pair${open ? ' open' : ''}`}
      id="hero-dd-sample-wrap"
    >
      <button
        type="button"
        className="hero-dd-trigger"
        id="hero-dd-sample-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        <span className="hero-dd-text">
          <span className="hero-dd-val" id="hero-dd-product-label">
            {product}{' '}
          </span>
          <span className="hero-dd-mid">
            {COST_BOM_DEMO_STRINGS.sourcingFrom}
          </span>
          <span className="hero-dd-meta" id="hero-dd-country-label">
            {country}
          </span>
        </span>
        <span className="hero-dd-caret">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="#868E96"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        className="hero-dd-pop"
        id="hero-dd-sample-pop"
        hidden={!open}
        role="listbox"
      >
        {SAMPLE_PRODUCTS.map((sample, idx) => (
          <div key={sample.key}>
            {idx > 0 && (
              <div
                className="hero-dd-divider"
                role="separator"
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              className={`hero-dd-opt${activeKey === sample.key ? ' active' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                pickSample(sample)
              }}
            >
              <span className="hero-dd-opt-name">{sample.product}</span>
              <span className="hero-dd-opt-mid">
                {COST_BOM_DEMO_STRINGS.sourcingFrom}
              </span>
              <span className="hero-dd-opt-country">{sample.country}</span>
            </button>
          </div>
        ))}
        <div className="hero-dd-divider" role="separator" aria-hidden="true" />
        <button
          type="button"
          className="hero-dd-opt hero-dd-opt--custom"
          onClick={(e) => {
            e.stopPropagation()
            openConversion()
          }}
        >
          <span className="hero-dd-opt-custom">
            <span className="hero-dd-opt-name">
              {COST_BOM_DEMO_STRINGS.yourOwnProduct}
            </span>
            <span className="hero-dd-opt-hint">
              {COST_BOM_DEMO_STRINGS.describeProduct}
            </span>
          </span>
        </button>
      </div>

      <input type="hidden" id="hero-dd-product" value={product} readOnly />
      <input type="hidden" id="hero-dd-country" value={country} readOnly />
    </div>
  )
}
